<template>
  <div class="fv-page py-3">
    <header class="fv-toolbar">
      <div class="fv-toolbar__title">
        <span class="fv-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Field Visits</h1>
          <p>Every visit captured from the Field Sales Form — sold or otherwise</p>
        </div>
      </div>
      <div class="fv-toolbar__actions">
        <CButton size="sm" class="fv-refresh-btn" :class="{ 'is-loading': isLoading }" :disabled="isLoading" @click="loadVisits()">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="fv-refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
        <CButton size="sm" class="fv-export-btn" :disabled="isExporting || !filters.fromDate || !filters.toDate" @click="exportCsv">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span>{{ isExporting ? 'Exporting…' : 'Export CSV' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="fv-alert">{{ errorMessage }}</CAlert>

    <section class="fv-section">
      <header class="fv-section__head">
        <h2>Filters</h2>
        <small class="fv-section__hint">{{ totalLabel }}</small>
      </header>
      <div class="fv-section__body">
        <div class="fv-filters">
          <div class="fv-field fv-field--range">
            <label class="fv-label">Date Range</label>
            <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          </div>
          <div class="fv-field">
            <label class="fv-label">Status</label>
            <AppDropdown v-model="filters.status" :options="statusOptions" placeholder="Any Status" />
          </div>
          <div class="fv-field fv-field--search">
            <label class="fv-label">Search</label>
            <div class="fv-search-row">
              <input v-model.trim="filters.search" type="text" class="fv-input" placeholder="Name, phone, address, district, pincode…" @keydown.enter="applyFilters">
              <CButton :disabled="isLoading" class="fv-apply-btn" @click="applyFilters">{{ isLoading ? '…' : 'Apply' }}</CButton>
              <CButton :disabled="isLoading" class="fv-reset-btn" @click="resetFilters">Reset</CButton>
            </div>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="Visits"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      :sortable="false"
      :show-count="false"
      empty-text="No field visits found for the current filters."
    >
      <template #toolbar>
        <span class="fv-table-hint">{{ pageRangeLabel }}</span>
      </template>

      <template #cell-status="{ row }">
        <span :class="['fv-pill', row.status_class]">{{ row.status_label || '—' }}</span>
      </template>

      <template #cell-order="{ row }">
        <span v-if="row.order_id" class="fv-order-link">#{{ row.order_id }}</span>
        <span v-else class="fv-order-link fv-order-link--no">—</span>
      </template>

      <template #cell-agro_type="{ value }">
        <span class="fv-agro-cell" :title="value || ''">{{ value || '—' }}</span>
      </template>

      <template #cell-address="{ value }">
        <span class="fv-address-cell" :title="value || ''">{{ value || '—' }}</span>
      </template>

      <template #cell-comments="{ value }">
        <span class="fv-comments-cell" :title="value || ''">{{ value || '—' }}</span>
      </template>

      <template #cell-map="{ row }">
        <a v-if="row.map_href" :href="row.map_href" target="_blank" rel="noopener" class="fv-map-btn">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span>Map</span>
        </a>
        <span v-else class="fv-map-btn fv-map-btn--disabled">—</span>
      </template>
    </BaseTable>

    <div v-if="!isLoading && visits.length && lastPage > 1" class="fv-pagination-bar">
      <span class="fv-pagination-info">{{ pageRangeLabel }}</span>
      <AppPager :current-page="currentPage" :last-page="lastPage" @change="goToPage" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'
import BaseTable from '@/components/BaseTable.vue'
import AppPager from '@/components/AppPager.vue'

const STATUS_OPTIONS = [
  { value: '', label: 'Any Status' },
  { value: 'sold', label: 'Sold' },
  { value: 'interested', label: 'Interested' },
  { value: 'interested_later', label: 'Interested Later' },
  { value: 'agency', label: 'Agency' }
]

export default {
  name: 'FieldVisits',
  components: { AppDropdown, DateRangePicker, BaseTable, AppPager },
  data () {
    const today = new Date().toISOString().slice(0, 10)
    const thirtyDaysAgo = new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    return {
      isLoading: false,
      isExporting: false,
      errorMessage: '',
      visits: [],
      total: 0,
      currentPage: 1,
      lastPage: 1,
      perPage: 25,
      statusOptions: STATUS_OPTIONS,
      filters: {
        fromDate: thirtyDaysAgo,
        toDate: today,
        status: '',
        search: ''
      },
      columns: [
        { key: 'sno', label: 'SNo', width: '64px' },
        { key: 'date_time', label: 'Date / Time', width: '160px' },
        { key: 'name', label: 'Name' },
        { key: 'phone', label: 'Phone', width: '130px' },
        { key: 'agro_type', label: 'Agriculture' },
        { key: 'address', label: 'Address' },
        { key: 'district', label: 'District' },
        { key: 'pincode', label: 'Pincode', width: '100px' },
        { key: 'status', label: 'Status', width: '130px' },
        { key: 'order', label: 'Order', width: '90px' },
        { key: 'comments', label: 'Notes' },
        { key: 'map', label: 'Map', width: '90px', align: 'center' }
      ]
    }
  },
  computed: {
    totalLabel () {
      return this.total ? `${this.total} total` : ''
    },
    pageRangeLabel () {
      if (!this.visits.length) return '0'
      const start = (this.currentPage - 1) * this.perPage + 1
      const end = start + this.visits.length - 1
      return `${start}–${end} of ${this.total}`
    },
    dateRange: {
      get () {
        return { fromDate: this.filters.fromDate, toDate: this.filters.toDate }
      },
      set (value) {
        this.filters.fromDate = (value && value.fromDate) || ''
        this.filters.toDate = (value && value.toDate) || ''
      }
    },
    tableRows () {
      const offset = (this.currentPage - 1) * this.perPage
      return this.visits.map((row, idx) => ({
        sno: offset + idx + 1,
        id: row.id,
        date_time: this.formatDateTime(row.created_at),
        name: row.name || '—',
        phone: row.phone || '—',
        agro_type: row.agro_type || '',
        address: row.address || '',
        district: row.district || '—',
        pincode: row.pincode || '—',
        status: row.status || '',
        status_label: this.statusLabel(row.status),
        status_class: this.statusClass(row.status),
        order: row.order_id,
        order_id: row.order_id,
        comments: row.comments || '',
        map_href: this.mapHref(row.gps_coordinates)
      }))
    }
  },
  created () {
    this.loadVisits()
  },
  methods: {
    formatDateTime (value) {
      if (!value) return ''
      const ms = typeof value === 'number' ? value * 1000 : Date.parse(value)
      if (!Number.isFinite(ms)) return String(value)
      const d = new Date(ms)
      const pad = (n) => String(n).padStart(2, '0')
      return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    statusLabel (status) {
      const found = this.statusOptions.find((o) => o.value === status)
      if (found && found.value) return found.label
      return String(status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    statusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s === 'sold') return 'is-success'
      if (s === 'interested') return 'is-info'
      if (s === 'interested_later') return 'is-warn'
      if (s === 'agency') return 'is-violet'
      return 'is-default'
    },
    mapHref (gps) {
      if (!gps) return ''
      const first = String(gps).split('|')[0].trim()
      if (!first) return ''
      const parts = first.split(',').map((p) => p.trim())
      if (parts.length < 2) return ''
      const lat = Number(parts[0])
      const lng = Number(parts[1])
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return ''
      return `https://www.google.com/maps?q=${lat},${lng}`
    },
    loadVisits (page = this.currentPage) {
      this.errorMessage = ''
      this.isLoading = true
      const params = {
        page,
        per_page: this.perPage,
        fromDate: this.filters.fromDate || undefined,
        toDate: this.filters.toDate || undefined,
        status: this.filters.status || undefined,
        search: this.filters.search || undefined
      }
      axios.get(`${this.$apiAdress}/api/field_visit`, { params })
        .then((response) => {
          const data = response.data || {}
          this.visits = Array.isArray(data.data) ? data.data : []
          this.currentPage = Number(data.current_page || 1)
          this.lastPage = Number(data.last_page || 1)
          this.total = Number(data.total || 0)
          this.perPage = Number(data.per_page || this.perPage)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load field visits.'
          this.visits = []
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    applyFilters () {
      this.currentPage = 1
      this.loadVisits(1)
    },
    resetFilters () {
      const today = new Date().toISOString().slice(0, 10)
      const thirtyDaysAgo = new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
      this.filters = { fromDate: thirtyDaysAgo, toDate: today, status: '', search: '' }
      this.currentPage = 1
      this.loadVisits(1)
    },
    goToPage (page) {
      if (page < 1 || page > this.lastPage) return
      this.loadVisits(page)
    },
    exportCsv () {
      if (!this.filters.fromDate || !this.filters.toDate) {
        this.errorMessage = 'Date range is required to export CSV.'
        return
      }
      this.errorMessage = ''
      this.isExporting = true
      axios.post(
        `${this.$apiAdress}/api/field-visit-report/`,
        { start_date: this.filters.fromDate, end_date: this.filters.toDate },
        { responseType: 'blob' }
      )
        .then((response) => {
          const blob = new Blob([response.data], { type: 'text/csv' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `field_visits_${this.filters.fromDate}_${this.filters.toDate}.csv`
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Unable to export CSV.'
        })
        .finally(() => {
          this.isExporting = false
        })
    }
  }
}
</script>

<style scoped>
.fv-page {
  padding: 0.25rem 0;
}

.fv-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.fv-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.fv-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.fv-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.fv-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.fv-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.fv-toolbar__actions >>> .btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.fv-toolbar__actions >>> .btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.fv-toolbar__actions >>> .btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.fv-toolbar__actions >>> .btn:focus { outline: none; }
.fv-toolbar__actions >>> .btn:focus-visible {
  outline: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.fv-refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.fv-toolbar__actions >>> .btn.fv-refresh-btn:hover:not(:disabled) .fv-refresh-btn__icon {
  transform: rotate(60deg);
}

.fv-toolbar__actions >>> .btn.fv-refresh-btn.is-loading .fv-refresh-btn__icon {
  animation: fv-spin 0.9s linear infinite;
}

@keyframes fv-spin { to { transform: rotate(360deg); } }

.fv-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.fv-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.fv-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.fv-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.fv-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.fv-section__body {
  padding: 1rem;
}

.fv-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.fv-input {
  width: 100%;
  height: 36px;
  padding: 0.3rem 0.6rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #2f3640;
  background: #ffffff;
}

.fv-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.fv-filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.85rem;
}

.fv-field {
  display: flex;
  flex-direction: column;
}

.fv-field--range {
  grid-column: span 2;
  min-width: 280px;
}

.fv-field--search {
  grid-column: 1 / -1;
}

.fv-search-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.fv-search-row .fv-input {
  flex: 1 1 220px;
  min-width: 200px;
}

.fv-search-row >>> .btn {
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0 1rem;
}

.fv-search-row >>> .btn.fv-apply-btn {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.fv-search-row >>> .btn.fv-apply-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.fv-search-row >>> .btn.fv-apply-btn:focus { outline: none; }
.fv-search-row >>> .btn.fv-apply-btn:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.fv-search-row >>> .btn.fv-apply-btn:disabled {
  opacity: 0.75;
  cursor: progress;
}

.fv-search-row >>> .btn.fv-reset-btn {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #4f5560;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.fv-search-row >>> .btn.fv-reset-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.fv-search-row >>> .btn.fv-reset-btn:focus { outline: none; }
.fv-search-row >>> .btn.fv-reset-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.fv-table-hint {
  font-size: 0.78rem;
  color: #6b7280;
}

.fv-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
}

.fv-pill.is-success { background: #dff5ea; color: #0e5b46; }
.fv-pill.is-info    { background: #e0edff; color: #1e40af; }
.fv-pill.is-warn    { background: #fef3c7; color: #92400e; }
.fv-pill.is-violet  { background: #ede9fe; color: #5b21b6; }
.fv-pill.is-default { background: #eef1f7; color: #4b5563; }

.fv-order-link {
  font-weight: 600;
  color: #0e5b46;
}

.fv-order-link--no {
  color: #9ca3af;
  font-weight: 500;
}

.fv-agro-cell,
.fv-address-cell,
.fv-comments-cell {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.fv-address-cell { max-width: 240px; }

.fv-map-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 26px;
  padding: 0 0.6rem;
  border-radius: 6px;
  border: 1px solid #c7ebda;
  background: #f5fbf8;
  color: #0e5b46;
  font-size: 0.76rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

.fv-map-btn:hover {
  background: #ecfdf5;
  border-color: #16a07a;
  transform: translateY(-1px);
  text-decoration: none;
}

.fv-map-btn:focus { outline: none; }
.fv-map-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.fv-map-btn--disabled {
  background: transparent;
  border-color: transparent;
  color: #9ca3af;
  cursor: default;
  font-weight: 500;
}

.fv-map-btn--disabled:hover {
  background: transparent;
  border-color: transparent;
  transform: none;
}

.fv-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.85rem;
}

.fv-pagination-info {
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
}
</style>
